import React from "react";
import { LocationName, LocationType } from "#app/components/location-type.tsx";
import { Badge } from "#app/components/ui/badge";
import { getMajorityNetworkName } from "#app/utils/network-name.tsx";

const StationComponent = ({ station }: any) => {
  const groupData = (data: any) => {
    const groupedData = data.reduce((acc: any, curr: any) => {
      curr.outlets.forEach((outlet: any) => {
        const key = `${outlet.connector}-${outlet.kilowatts}`;
        if (!acc[key]) {
          acc[key] = {
            connector: outlet.connector,
            kilowatts: outlet.kilowatts,
            quantity: 0,
          };
        }
        acc[key].quantity += 1;
      });
      return acc;
    }, {});

    return Object.values(groupedData);
  };

  const ratingPercentage =
    station.negatives + station.positives > 0
      ? (station.positives / (station.negatives + station.positives)) * 100
      : 200;

  const getBackgroundColor = (percentage: number) => {
    const hue = ((percentage / 100) * 120).toString(10);
    return ["hsl(", hue, ",100%,50%)"].join("");
  };

  const isPublic = station.icon_type === "G" || station.icon_type === "Y";
  const stationCount = station.stations.length;
  const connectorData = groupData(station.stations);

  return (
    <div className="mt-4 w-full items-center overflow-clip rounded-lg border border-border bg-background p-4 shadow-md hover:md:opacity-90 hover:md:shadow-lg">
      <div className="flex flex-col items-center justify-center md:flex-row md:justify-start md:space-x-4">
        <div className="hidden w-full items-center justify-center rounded-full bg-muted/50 p-2 dark:bg-muted/30 md:flex md:w-28">
          <LocationType type={station.type} />
        </div>
        <div className="flex h-full w-full flex-col">
          <h2 className="pt-4 text-lg font-semibold md:pt-0">
            {station.name.replace("(Coming Soon)", "(Em Breve)")}
          </h2>
          <span>
            <LocationName type={station.type} />
          </span>
          {station.address && (
            <p className="text-sm capitalize text-muted-foreground">
              {station.address}
            </p>
          )}
          <div className="flex flex-wrap gap-2 py-4">
            <Badge className={isPublic ? "bg-green-500" : "bg-yellow-500"}>
              {isPublic ? "Carregador Público" : "Uso Restrito"}
            </Badge>
            {station.icon_type.includes("R") && (
              <Badge className="bg-red-500">Em Manutenção</Badge>
            )}
            {station.icon_type === "Y" && (
              <Badge className="bg-blue-500">Carregador Rápido</Badge>
            )}
          </div>
        </div>
        <div className="flex w-full flex-grow">
          <div className="flex flex-col">
            {station.majorityNetworkId &&
              getMajorityNetworkName(station.majorityNetworkId) !==
                "Unknown" && (
                <Badge variant="outline" className="mb-2 w-fit">
                  Rede {getMajorityNetworkName(station.majorityNetworkId)}
                </Badge>
              )}
            <p className="font-bold text-primary">
              {`${stationCount} ${stationCount > 1 ? "Estações" : "Estação"}`}
            </p>
            {connectorData.map((outlet: any, index: number) => (
              <div
                key={`${station.id}-${outlet.connector}-${outlet.kilowatts}-${index}`}
                className="text-sm"
              >
                {`${outlet.quantity} ${
                  outlet.quantity > 1 ? "Conectores" : "Conector"
                } ${outlet.connector === 7 ? "Tipo 2" : "CCS"} ${
                  outlet.kilowatts ? `de ${outlet.kilowatts} kW` : ""
                }`}
              </div>
            ))}
          </div>
        </div>
        <div className="flex w-full flex-shrink-0 flex-col pt-2 text-sm md:w-48 md:justify-end md:pt-0">
          <div className="justify-between">
            {station.distance && (
              <p className="pb-2 pt-6 text-center font-bold uppercase md:pt-0 md:text-left">
                Distância: {station.distance.toFixed(2)} km
              </p>
            )}
            {ratingPercentage > 100 ? (
              <Badge variant="secondary" className="w-full justify-center">
                Sem avaliações
              </Badge>
            ) : (
              <Badge
                variant="outline"
                className="w-full justify-center"
                style={{
                  backgroundColor: getBackgroundColor(ratingPercentage),
                  color: "black",
                }}
              >
                <span>{Math.round(ratingPercentage)}% positivas</span>
                <span className="ml-1 text-xs">
                  ({station.positives + station.negatives})
                </span>
              </Badge>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Station = React.memo(StationComponent);
export default Station;
